import { useCallback, useEffect, useMemo } from 'react';

import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useParams } from 'next/navigation';

import { FiltersInterface } from '@contexts/hotel/HotelListing';
import { HotelListFiltersViewModel, HotelListViewModel } from '@api/index';
import { callService } from '@api/caller';
import clean from '@utils/cleanObject';
import { ListingMetaViewModel } from '@components/views/types';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { buildBackendRoomArray } from '@components/views/hotel/utils';
import { formatStringToArray } from '@utils/utils';

interface useListingFetchingProps {
  setAdultCount: React.Dispatch<React.SetStateAction<number>>;
  setChildCount: React.Dispatch<React.SetStateAction<number>>;
  setFilters: React.Dispatch<React.SetStateAction<Partial<FiltersInterface>>>;
  basketId?: string;
  onSuccessfulFetch?: (data: InfiniteData<HotelListViewModel>) => void;
  onErroredFetch?: () => void;
}

export const useListingFetching = ({
  setAdultCount,
  setChildCount,
  setFilters,
  onSuccessfulFetch,
  onErroredFetch,
}: useListingFetchingProps) => {
  const query = useSearchParamsToQuery();
  const params = useParams();
  const slug = params?.slug as string;

  const fetchHotelItems = useCallback(
    (pageParam?: number, basketId?: string) => {
      const formattedRoom = buildBackendRoomArray(query?.room);

      return callService(
        'HotelsService',
        'listHotels',
        clean({
          _in: query.in ? String(query.in) : undefined,
          out: query.out as string,
          slug,
          sort: query.sort as string,
          room: formattedRoom,
          keywords: query.keywords as string,
          priceMin: query.priceMin as string,
          priceMax: query.priceMax as string,
          page: pageParam,
          size: query.size as string,
          region: formatStringToArray(query.region),
          theme: formatStringToArray(query.theme),
          popularity: formatStringToArray(query.popularity),
          status: query.status as string,
          boardType: formatStringToArray(query.boardType),
          refundable: query.refundable as string,
          point: formatStringToArray(query.point),
          star: formatStringToArray(query.star),
          category: formatStringToArray(query.category),
          feature: formatStringToArray(query.feature),
          hotelAccommodationTaxType: query.accommodationTaxType as string,
          ...(basketId ? { basketId } : {}),
        })
      );
    },
    [query, slug]
  );

  const fetchHotels = ({ pageParam = 0 }: { pageParam?: number }) => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');

    return fetchHotelItems(pageParam, String(basketId));
  };

  const {
    data,
    isError,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery(['hotelListing', slug, query], fetchHotels, {
    enabled: Boolean(slug),
    getNextPageParam: (lastPage) => {
      if (
        (lastPage.meta?.currentPage || 0) < (lastPage.meta?.totalPages || 0)
      ) {
        return (lastPage.meta?.currentPage || 0) + 1;
      }
      return false;
    },

    onSuccess: onSuccessfulFetch,
    onError: onErroredFetch,
  });

  useEffect(() => {
    if (query && query.room) {
      const roomsAdult = query.room
        .toString()
        .split(',')
        .map((item) => (item?.includes('-') ? '' : item));

      const roomsChild = query.room
        .toString()
        .split(',')
        .map((item) => (item?.includes('-') ? item : ''))
        .filter((item) => item !== '');

      const adultNumber = roomsAdult.reduce((a, b) => Number(a) + Number(b), 0);

      setAdultCount(adultNumber);
      setChildCount(roomsChild.length);
    }

    if (query.keywords) {
      setFilters((prev) => ({
        ...prev,
        hotelName: String(query.keywords),
      }));
    }
  }, [query]);

  useEffect(() => {
    if (!data) return;

    if (isError) return;

    setFilters((prev) => ({
      ...prev,
      regions:
        (data.pages?.length &&
          data.pages[0].filters?.filter(
            (item: HotelListFiltersViewModel) => item.type === 'region'
          )[0]?.items) ||
        [],
    }));
  }, [data]);

  const notFound = useMemo(() => {
    return (
      data?.pages?.every(
        (page) => Array.isArray(page.data) && page.data.length === 0
      ) || false
    );
  }, [data]);
  const meta = useMemo(() => {
    const meta = data?.pages?.[data?.pages?.length - 1]?.meta;
    let metaData: ListingMetaViewModel;
    if (meta) {
      metaData = { ...meta, hasNextPage: !!hasNextPage };
      return metaData;
    }
  }, [data, hasNextPage]);

  return {
    hotels: data?.pages?.map((hotels) => hotels.data),
    filters: data?.pages?.[0]?.filters ?? [],
    isError: !isLoading && !data?.pages[0]?.data?.length ? true : isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    refetch,
    meta,
    isFetchingNextPage,
    notFound,
  };
};
